import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@env/environment';
import { CollaborateurService } from '@shared/services/cabinet/collaborateur.service';
import { DossierService } from '@shared/services/cabinet/dossier.service';
import { ArchiveUtil } from '@shared/utilitaires/archive.util';
import { dateSituationCollab, PARAM_ARCHIVE, PARAM_SITUATION } from '@app/archive-situation';
import { PORTEE_CABINET } from './contexte-http';

/**
 * En-têtes du dossier courant ajoutés aux appels (fusion des anciens dossierInterceptor et userClientInterceptor,
 * en-têtes envoyés inchangés) :
 * - client avec un dossier courant : X-CABINET (cabinet du dossier) sur tous les appels, sauf s'il est déjà posé ;
 * - API Cabinet sans X-DOSSIER posé par l'appel : X-DOSSIER, X-PASSWORD et X-NATURE (DossierService.ajouterEntetesDossier) ;
 *   en mode archive (?dateArchive=, collaborateur seulement), X-DATEARCHIVE et adresse préfixée par « Archive » pour les
 *   appels autorisés.
 * - collaborateur dans un dossier de situation (?situation=aaaammjj) : X-SITUATION sur les appels à l'API Cabinet, comme le
 *   bureau (core/archive-situation.ts).
 * Partent sans aucun de ces en-têtes : les lectures « ForUser » (droits de l'utilisateur, avant tout dossier) et les
 * appels de portée cabinet (contexteDePortee('cabinet'), contexte-http.ts) : le dossier courant reste en mémoire après
 * être sorti du dossier, il ne doit pas partir avec un appel du Paramétrage, des Outils ou de la Gestion interne.
 */
export const dossierInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  if (estAppelSansDossier(req) || req.context.get(PORTEE_CABINET)) {
    return next(req);
  }

  const dossierService = inject(DossierService);
  let headers = req.headers;
  let url = req.url;

  const collaborateurService = inject(CollaborateurService);
  const cabinetId = dossierService.cabinetId;
  if (collaborateurService.isClient && dossierService.dossierId && cabinetId && !headers.has('X-CABINET')) {
    headers = headers.append('X-CABINET', cabinetId.toString());
  }

  if (url.startsWith(environment.apiUrl.cabinet) && !headers.has('X-DOSSIER')) {
    // Modes archive et situation : fonctions du collaborateur seulement (?dateArchive= ou ?situation= tapé par un client :
    // ignoré) ; la constante écrite ici écarte ArchiveUtil du build « client seul » (core/fonctionnalites.ts).
    const parametres =
      LOGAIA_COLLAB_BUILD && collaborateurService.isCollab ? inject(ActivatedRoute).snapshot.queryParams : {};
    const dateArchive: string | undefined = parametres[PARAM_ARCHIVE];
    const adresse = new URL(req.url);
    const archive = LOGAIA_COLLAB_BUILD && !!dateArchive && ArchiveUtil.isAuthorized(req.method, adresse.pathname);
    headers = dossierService.ajouterEntetesDossier(headers, dossierService.dossierId, archive ? dateArchive : undefined);
    const situation = LOGAIA_COLLAB_BUILD ? dateSituationCollab(true, parametres[PARAM_SITUATION]) : undefined;    if (LOGAIA_COLLAB_BUILD && situation && !headers.has('X-SITUATION')) {
      headers = headers.append('X-SITUATION', situation);
    }
    if (archive) {
      adresse.pathname = `Archive${adresse.pathname}`;
      url = adresse.href;
    }
  }

  return next(req.clone({ headers, url }));
};

/** Lecture des droits de l'utilisateur (GET …ForUser…) : aucun en-tête de dossier. */
function estAppelSansDossier(req: HttpRequest<unknown>): boolean {
  return req.method === 'GET' && /ForUser/.test(req.url);
}
